import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useTranslation, useLanguage } from "../i18n/LanguageContext";
import logoSrc from "../assets/logo-rava.png";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const t = useTranslation();
  const { lang, setLang } = useLanguage();

  const links = [
    { label: t.nav_home, to: "/" },
    { label: t.nav_about, to: "/a-propos" },
    { label: t.nav_services, to: "/services" },
    { label: t.nav_projects, to: "/realisations" },
    { label: t.nav_contact, to: "/contact" },
  ];

  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [location.pathname]);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const isActive = (to: string) => to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 bg-white border-b border-border transition-shadow duration-200 ${scrolled ? "shadow-[0_1px_3px_rgba(0,0,0,0.05)]" : ""}`}>
        <div className="max-w-content mx-auto px-6 md:px-12 flex items-center justify-between h-20">
          <Link to="/">
            <img src={logoSrc} alt="RAVA" className="h-16" />
          </Link>

          <div className="hidden lg:flex items-center gap-10">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="relative text-[13px] uppercase tracking-[0.08em] font-medium transition-colors duration-300 group"
                style={{ color: isActive(l.to) ? "#111827" : "#6B7280" }}
              >
                {l.label}
                <span className={`absolute left-0 -bottom-1 h-[2px] bg-accent transition-all duration-300 ${isActive(l.to) ? "w-full" : "w-0 group-hover:w-full"}`} />
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-5">
            <div className="flex text-[13px] uppercase tracking-[0.08em] font-medium text-secondary">
              <button onClick={() => setLang("fr")} className={`transition-colors duration-300 ${lang === "fr" ? "text-primary" : "hover:text-primary"}`}>FR</button>
              <span className="mx-2 text-border">|</span>
              <button onClick={() => setLang("en")} className={`transition-colors duration-300 ${lang === "en" ? "text-primary" : "hover:text-primary"}`}>EN</button>
            </div>
            <Link to="/contact" className="text-[13px] uppercase tracking-[0.08em] font-semibold text-white bg-accent px-7 py-3 hover:bg-accent-dark transition-colors duration-300">
              {t.nav_cta}
            </Link>
          </div>

          <button onClick={() => setOpen(!open)} className="lg:hidden w-10 h-10 flex items-center justify-center text-primary" aria-label="Menu">
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </nav>

      {/* Mobile panel */}
      <div className={`fixed top-0 right-0 bottom-0 w-80 max-w-[85vw] bg-white z-[60] border-l border-border transition-transform duration-300 ease-in-out ${open ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex justify-end p-6">
          <button onClick={() => setOpen(false)} className="w-10 h-10 flex items-center justify-center text-primary" aria-label="Fermer">
            <X size={22} strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex flex-col px-8 gap-2">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`py-3 text-[15px] uppercase tracking-[0.08em] font-medium border-b border-border transition-colors duration-300 ${isActive(l.to) ? "text-primary" : "text-secondary hover:text-primary"}`}
            >
              {l.label}
            </Link>
          ))}
          <div className="flex items-center gap-4 py-4 text-[13px] uppercase tracking-[0.08em] font-medium text-secondary">
            <button onClick={() => setLang("fr")} className={lang === "fr" ? "text-primary" : ""}>FR</button>
            <span className="text-border">|</span>
            <button onClick={() => setLang("en")} className={lang === "en" ? "text-primary" : ""}>EN</button>
          </div>
          <Link to="/contact" className="mt-4 text-center text-[13px] uppercase tracking-[0.08em] font-semibold text-white bg-accent px-7 py-4">
            {t.nav_cta}
          </Link>
        </div>
      </div>
      {open && <div className="fixed inset-0 bg-black/20 z-[55]" onClick={() => setOpen(false)} />}
    </>
  );
}
